'use client';

import { useEffect, useRef, useState } from 'react';
import { useRealtimeAlerts } from '@/lib/realtime';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPrice } from '@/lib/format';
import type { PriceAlert, AlertCondition } from '@glintfans/shared';

interface Toast {
  id: number;
  alert: PriceAlert;
}

/** System-style popup shown when a price alert triggers (top-right toasts). */
export default function AlertToaster() {
  const { language } = useAppStore();
  const t = translate(language);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 10_000);
    return () => clearTimeout(timer);
  }, [toasts]);

  useRealtimeAlerts((alert) => {
    idRef.current += 1;
    setToasts((prev) => [...prev.slice(-2), { id: idRef.current, alert }]);
  });

  const conditionText = (a: PriceAlert): string => {
    const map: Record<AlertCondition, string> = {
      price_above: t.alerts.priceAbove,
      price_below: t.alerts.priceBelow,
      change_percent_above: t.alerts.changeAbove,
      change_percent_below: t.alerts.changeBelow,
      volume_spike: t.alerts.volumeSpike,
      cross_above: t.alerts.crossAbove,
      cross_below: t.alerts.crossBelow,
      funding_above: t.alerts.fundingAbove,
      funding_below: t.alerts.fundingBelow,
      oi_above: t.alerts.oiAbove,
      oi_below: t.alerts.oiBelow,
      rsi_above: t.alerts.rsiAbove,
      rsi_below: t.alerts.rsiBelow,
    };
    return map[a.condition] ?? a.condition;
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 top-16 z-[100] flex w-80 flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto rounded-lg border border-gf-warn/60 bg-gf-surface/95 p-4 shadow-2xl backdrop-blur"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.35)' }}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="rounded bg-gf-warn/20 px-1.5 py-0.5 text-xs font-semibold text-gf-warn">
              {t.alerts.triggered}
            </span>
            <button
              className="text-xs text-gf-muted hover:text-gf-text"
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== toast.id))}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="mt-2 text-sm font-semibold">{toast.alert.symbol}</div>
          <div className="mt-1 text-xs text-gf-muted">
            {conditionText(toast.alert)} {formatPrice(toast.alert.targetValue)}
          </div>
        </div>
      ))}
    </div>
  );
}
